import { defaultConfig } from '../../../../../utils/config';

export interface HideMdOptions {
  emphasis?: boolean;
  codeSpan?: boolean;
  codeFence?: boolean;
  links?: boolean;
}

const defaultOptions: HideMdOptions = {
  emphasis: true,
  codeSpan: true,
  codeFence: true,
  links: true,
};

function countBackslashesBefore(input: string, index: number): number {
  let count = 0;
  let i = index - 1;
  while (i >= 0 && input[i] === '\\') {
    count++;
    i--;
  }
  return count;
}

function isEscaped(input: string, index: number): boolean {
  return countBackslashesBefore(input, index) % 2 === 1;
}

function isWordChar(ch: string | undefined): boolean {
  if (!ch) return false;
  return /[A-Za-z0-9]/.test(ch);
}

function isIntrawordUnderscoreSingle(input: string, index: number): boolean {
  const left = index > 0 ? input[index - 1] : undefined;
  const right = index + 1 < input.length ? input[index + 1] : undefined;
  return isWordChar(left) && isWordChar(right);
}

function isIntrawordUnderscoreDouble(input: string, index: number): boolean {
  // index points to first underscore of a "__" run
  const left = index > 0 ? input[index - 1] : undefined;
  const right = index + 2 < input.length ? input[index + 2] : undefined;
  return isWordChar(left) && isWordChar(right);
}

// Returns a stable prefix by trimming from the first unmatched opener to the end.
export default function hideIncompleteMarkdown(input: string, options?: HideMdOptions): string {
  if (!input) return '';
  if (!defaultConfig.STREAM_HIDE_INCOMPLETE_MARKDOWN) return input;
  
  const opts = { ...defaultOptions, ...(options || {}) };

  const len = input.length;

  // States
  let inFence = false;
  let fenceStart = -1;
  let inlineCodeStart = -1;

  // token stacks
  const stack: Record<string, number[]> = {
    '**': [],
    '*': [],
    '__': [],
    '_': [],
    '~~': [],
  };

  const bracketStack: number[] = [];
  let linkParenDepth = 0;
  let linkParenStart = -1;

  const pushToken = (tok: string, index: number) => {
    const s = stack[tok];
    if (!s) return;
    if (s.length > 0) s.pop(); else s.push(index);
  };

  let i = 0;
  while (i < len) {
    const ch = input[i];

    // Code fence ```
    if (opts.codeFence && !inFence && ch === '`' && !isEscaped(input, i)) {
      if (i + 2 < len && input[i + 1] === '`' && input[i + 2] === '`') {
        inFence = true;
        fenceStart = i;
        i += 3;
        continue;
      }
    } else if (opts.codeFence && inFence && ch === '`') {
      if (i + 2 < len && input[i + 1] === '`' && input[i + 2] === '`') {
        inFence = false;
        fenceStart = -1;
        i += 3;
        continue;
      }
      i++;
      continue;
    }

    if (inFence) {
      i++;
      continue;
    }

    // Inline code `
    if (opts.codeSpan && ch === '`' && !isEscaped(input, i)) {
      if (inlineCodeStart === -1) inlineCodeStart = i; else inlineCodeStart = -1;
      i++;
      continue;
    }

    // Links
    if (opts.links && ch === '[' && !isEscaped(input, i) && inlineCodeStart === -1) {
      bracketStack.push(i);
      i++;
      continue;
    }
    if (opts.links && ch === ']' && !isEscaped(input, i) && inlineCodeStart === -1) {
      if (bracketStack.length > 0) bracketStack.pop();
      // If next char is '(' start tracking link url parentheses
      if (i + 1 < len && input[i + 1] === '(') {
        linkParenDepth = 1;
        linkParenStart = i + 1;
        i += 2;
        continue;
      }
      i++;
      continue;
    }
    if (opts.links && linkParenDepth > 0) {
      if (ch === '(') linkParenDepth++;
      else if (ch === ')') linkParenDepth--;
      i++;
      continue;
    }

    if (opts.emphasis && inlineCodeStart === -1) {
      // Two-char tokens first
      if (ch === '~' && i + 1 < len && input[i + 1] === '~' && !isEscaped(input, i)) {
        pushToken('~~', i);
        i += 2;
        continue;
      }
      if (ch === '*' && i + 1 < len && input[i + 1] === '*' && !isEscaped(input, i)) {
        pushToken('**', i);
        i += 2;
        continue;
      }
      if (ch === '_' && i + 1 < len && input[i + 1] === '_' && !isEscaped(input, i)) {
        // Treat double underscore as intraword only when both sides are alphanumeric
        if (isIntrawordUnderscoreDouble(input, i)) {
          i += 2;
          continue;
        }
        pushToken('__', i);
        i += 2;
        continue;
      }
      // Single-char tokens
      if (ch === '*' && !isEscaped(input, i)) {
        pushToken('*', i);
        i++;
        continue;
      }
      if (ch === '_' && !isEscaped(input, i)) {
        // Ignore intraword single underscores like SNAKE_CASE
        if (isIntrawordUnderscoreSingle(input, i)) {
          i++;
          continue;
        }
        pushToken('_', i);
        i++;
        continue;
      }
    }

    i++;
  }

  let cutIndex = len;
  if (opts.codeFence && fenceStart !== -1) cutIndex = Math.min(cutIndex, fenceStart);
  if (opts.codeSpan && inlineCodeStart !== -1) cutIndex = Math.min(cutIndex, inlineCodeStart);
  if (opts.emphasis) {
    for (const key of Object.keys(stack)) {
      const s = (stack as any)[key] as number[];
      if (s.length > 0) cutIndex = Math.min(cutIndex, s[0]);
    }
  }
  if (opts.links) {
    if (bracketStack.length > 0) cutIndex = Math.min(cutIndex, bracketStack[0]);
    if (linkParenDepth > 0 && linkParenStart !== -1) cutIndex = Math.min(cutIndex, linkParenStart);
  }

  if (cutIndex < len) return input.slice(0, cutIndex);
  return input;
}


